/**
 * PurchaseOptionsElement - Custom HTML Element for handling purchase options
 * Handles PDP purchase options UI logic with product toggle support
 */
class PurchaseOptionsElement extends HTMLElement {
  constructor() {
    super();

    // State management
    this.isActive = false;
    this.mutationObserver = null;

    // Cached DOM elements
    this.elements = {};

    // Bind methods to preserve 'this' context
    this.handleTabChange = this.handleTabChange.bind(this);
    this.handleSubscriptionChange = this.handleSubscriptionChange.bind(this);
    this.handleOnetimeChange = this.handleOnetimeChange.bind(this);
    this.handleFormSubmit = this.handleFormSubmit.bind(this);
    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.handleExternalProductChange = this.handleExternalProductChange.bind(this);
    this.handleActiveStateChange = this.handleActiveStateChange.bind(this);
  }

  /**
   * Called when element is added to DOM
   */
  connectedCallback() {
    // Expose methods on the element for external access
    this.forceActivate = (defaultToSubscription = true) => {
      this.classList.add('active');
      this.resetToDefaultState(defaultToSubscription);
    };

    this.forceUpdate = () => {
      this.detectActiveState();
      if (this.isActive) {
        this.updatePurchaseType();
      }
    };

    // Use setTimeout to ensure the element is fully rendered in the DOM
    setTimeout(() => {
      this.initializeComponent();
    }, 50);
  }

  /**
   * Called when element is removed from DOM
   */
  disconnectedCallback() {
    this.removeEventListeners();
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }

  /**
   * Initialize the component and cache DOM elements
   */
  initializeComponent() {
    this.cacheElements();
    this.detectActiveState();
    this.attachEventListeners();
    this.initializeOptions();
    this.setupMutationObserver();
  }

  /**
   * Cache frequently used DOM elements
   */
  cacheElements() {
    // Tab elements (within this element) - support both buttons and inputs
    this.elements.tabInputs = this.querySelectorAll('.purchase-options__tab-input');
    this.elements.tabButtons = this.querySelectorAll('.purchase-options__tab-nav');
    this.elements.subscriptionOptions = this.querySelectorAll('.subscription-option__input');
    this.elements.onetimeOptions = this.querySelectorAll('.onetime-option__input');

    // Find the closest form - could be ancestor or sibling
    const form = this.closest('form') || this.parentElement?.closest('form') || this.parentElement?.querySelector('form');
    
    // If we have a form context, query within it; otherwise fallback to scoped queries
    const queryContext = form || this.parentElement || document;
    
    // Form elements (scope to form/container to support multiple instances)
    this.elements.sellingPlanInput = queryContext.querySelector('input[name="selling_plan"]');
    this.elements.quantityInput = queryContext.querySelector('input[name="quantity"]');
    this.elements.monthsInput = queryContext.querySelector('input[name="properties[_months]"]') ||
      queryContext.querySelector('input[name="properties[_otp]"]');
    this.elements.variantInput = queryContext.querySelector('input[name="id"]');

    // Button elements (scope to container to support multiple instances)
    this.elements.submitButton = queryContext.querySelector('.purchase-summary__add-to-cart');
    this.elements.buttonSpinner = queryContext.querySelector('.loading-overlay__spinner');
    this.elements.buttonText = queryContext.querySelector('.purchase-summary__button-text');

    // Price elements (scope to container to support multiple instances)
    this.elements.totalPriceElement = queryContext.querySelector('.purchase-summary__total-price');
    this.elements.originalPriceElement = queryContext.querySelector('.purchase-summary__original-price');
    this.elements.originalAmountElement = queryContext.querySelector('.purchase-summary__original-amount');
    this.elements.savingsElement = queryContext.querySelector('.purchase-summary__savings');
    this.elements.savingsAmountElement = queryContext.querySelector('.purchase-summary__savings-amount');
    this.elements.buttonPrice = queryContext.querySelector('.button-price');
    this.elements.buttonComparePrice = queryContext.querySelector('.button-compare-price');

    // Notification elements (search document-wide as they're global/shared)
    this.elements.cartNotificationWrapper = document.querySelector('.cart-notification-wrapper');
    this.elements.cartNotificationProductName = document.querySelector('.cart-notification__product-name');
    this.elements.errorNotificationWrapper = document.querySelector('.error-notification-wrapper');
    this.elements.errorNotificationText = document.querySelector('.error-notification__text');
    
    // Log caching info for debugging
    console.log('Cached elements for:', this.id, {
      hasForm: !!form,
      queryContext: form ? 'form' : (this.parentElement ? 'parent' : 'document'),
      hasVariantInput: !!this.elements.variantInput,
      hasSubmitButton: !!this.elements.submitButton
    });
  }

  /**
   * Detect if this purchase option element is currently active
   */
  detectActiveState() {
    try {
      // Check if this element has the 'active' class or is the only purchase option
      // Also check if the parent container is active (for product toggle scenarios)
      const parentContainer = this.closest('[id^="purchase-option-"]');
      const hasActiveClass = this.classList.contains('active');
      const parentIsActive = parentContainer ? parentContainer.classList.contains('active') : false;
      const isOnlyOption = document.querySelectorAll('purchase-options').length === 1;

      // For product toggle scenarios, also check if this is the first/default element when no active state is set
      let isDefaultActive = false;
      if (!hasActiveClass && !parentIsActive && !isOnlyOption) {
        // If this is a product toggle scenario and no element is explicitly active,
        // consider the first purchase-options element as active
        const allPurchaseOptions = document.querySelectorAll('purchase-options');
        if (allPurchaseOptions.length > 0 && allPurchaseOptions[0] === this) {
          // Check if any parent container is marked as active
          const anyParentActive = Array.from(allPurchaseOptions).some(option => {
            const container = option.closest('[id^="purchase-option-"]');
            return container && container.classList.contains('active');
          });

          // If no parent is active, this first element should be considered active
          if (!anyParentActive) {
            isDefaultActive = true;
          }
        }
      }

      const previousState = this.isActive;
      this.isActive = hasActiveClass || parentIsActive || isOnlyOption || isDefaultActive;

      // Log state changes
      if (previousState !== this.isActive) {
        console.log('Active state CHANGED for element:', this.id, 'from', previousState, 'to', this.isActive);
      }

      console.log('Active state detection:', {
        element: this.id,
        hasActiveClass,
        parentIsActive,
        isOnlyOption,
        isDefaultActive,
        finalState: this.isActive,
        stateChanged: previousState !== this.isActive
      });

      return this.isActive;

    } catch (error) {
      console.error('Error in detectActiveState for element:', this.id, error);
      // Fallback: assume not active if there's an error
      this.isActive = false;
      return false;
    }
  }

  /**
   * Attach all event listeners
   */
  attachEventListeners() {
    // Tab switching listeners - support both inputs and buttons
    this.elements.tabInputs.forEach(input => {
      input.addEventListener('change', this.handleTabChange);
    });

    // Add button click listeners
    this.elements.tabButtons.forEach(button => {
      button.addEventListener('click', this.handleTabChange);
    });

    // Form submission listener (only if we have a form within this element)
    const form = this.closest('form')
    if (form) {
      console.log('Adding form submit listener to element:', this.id);
      form.addEventListener('submit', this.handleFormSubmit);
    }

    // Variant change listeners
    window.addEventListener('variant:change', this.handleVariantChange);
    document.addEventListener('variant:change', this.handleVariantChange);

    // External product change listeners
    document.addEventListener('product-tab:change', this.handleExternalProductChange);
    document.addEventListener('product-tab:switched', this.handleExternalProductChange);
    document.addEventListener('purchase-option:activate', this.handleExternalProductChange);

    // Use event delegation for dynamic option changes
    this.addEventListener('change', this.handleOptionChange.bind(this));
  }

  /**
   * Remove event listeners (cleanup)
   */
  removeEventListeners() {
    window.removeEventListener('variant:change', this.handleVariantChange);
    document.removeEventListener('variant:change', this.handleVariantChange);
    document.removeEventListener('product-tab:change', this.handleExternalProductChange);
    document.removeEventListener('product-tab:switched', this.handleExternalProductChange);
    document.removeEventListener('purchase-option:activate', this.handleExternalProductChange);
  }

  /**
   * Handle option changes using event delegation
   */
  handleOptionChange(e) {
    if (e.target.matches('.subscription-option__input')) {
      this.handleSubscriptionChange(e);
    } else if (e.target.matches('.onetime-option__input')) {
      this.handleOnetimeChange(e);
    }
  }

  /**
   * Handle tab switching between subscription and one-time
   */
  handleTabChange(e) {
    console.log('Tab change event triggered for:', this.id);

    // If this is a button click, handle button activation
    if (e && e.target && e.target.classList.contains('purchase-options__tab-nav')) {
      // Clear all button active states
      this.elements.tabButtons.forEach(btn => {
        btn.classList.remove('active');
        btn.setAttribute('aria-selected', 'false');
      });

      // Activate clicked button
      e.target.classList.add('active');
      e.target.setAttribute('aria-selected', 'true');
    }

    this.handleTabSwitch();

    // Only update shared inputs if this element is active
    if (this.isActive) {
      this.updatePurchaseType();
    }
  }

  /**
   * Handle subscription option selection
   */
  handleSubscriptionChange(e) {
    const input = e.target;

    // Remove selected class from all subscription options in this element
    this.querySelectorAll('.subscription-option').forEach(option => {
      option.classList.remove('subscription-option--selected');
    });

    // Also deselect any OTP/onetime options (from OTP block or inline options)
    this.querySelectorAll('.onetime-option').forEach(option => {
      option.classList.remove('onetime-option--selected');
      const otpInput = option.querySelector('.onetime-option__input');
      if (otpInput) otpInput.checked = false;
    });

    // Add selected class to current option
    input.closest('.subscription-option').classList.add('subscription-option--selected');

    // Update hidden inputs and pricing only if this purchase option is active
    if (this.isActive) {
      this.updatePurchaseType();
    }
  }

  /**
   * Handle one-time option selection
   */
  handleOnetimeChange(e) {
    const input = e.target;

    // Remove selected class from all onetime options in this element
    this.querySelectorAll('.onetime-option').forEach(option => {
      option.classList.remove('onetime-option--selected');
    });

    // Also deselect any subscription options
    this.querySelectorAll('.subscription-option').forEach(option => {
      option.classList.remove('subscription-option--selected');
      const subInput = option.querySelector('.subscription-option__input');
      if (subInput) subInput.checked = false;
    });

    // Add selected class to current option
    input.closest('.onetime-option').classList.add('onetime-option--selected');

    // Update hidden inputs and pricing only if this purchase option is active
    if (this.isActive) {
      this.updatePurchaseType();
    }
  }

  /**
   * Handle form submission to cart
   */
  handleFormSubmit(e) {
    // Only process if this element is active
    if (!this.isActive) {
      console.log('Ignoring submit from inactive element:', this.id);
      return;
    }
    
    e.preventDefault();
    
    // Check if we should proceed to checkout after adding to cart
    const proceedToCheckout = this.elements.submitButton?.dataset.proceedToCheckout === 'true';
    
    // Show loading state
    this.setLoadingState(true);
    this.hideNotifications();
    const form = this.closest('form')

    // Get form data
    const formData = new FormData(form);

    // Submit to cart
    fetch('/cart/add.js', {
      method: 'POST',
      body: formData
    })
      .then(response => response.json())
      .then(data => {
        if (data.status && data.status === 422) {
          throw new Error(data.description || 'Unable to add item to cart');
        }

        console.log('Product added to cart:', data);

        // If proceed to checkout is enabled, redirect immediately
        if (proceedToCheckout) {
          // Small delay to ensure cart is updated server-side
          setTimeout(() => {
            window.location.href = '/checkout';
          }, 300);
          return; // Don't show notification or update UI, we're redirecting
        }

        // Normal flow: show notification and update cart
        this.showSuccessNotification(data);

        // Update cart count if available
        if (window.theme && window.theme.updateCartCount) {
          window.theme.updateCartCount();
        }

        // Trigger cart update events
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:refresh', {
            bubbles: true
          })
        );
      })
      .catch(error => {
        console.error('Error adding to cart:', error);
        this.showErrorNotification(
          error.message || 'Failed to add item to cart. Please try again.'
        );
      })
      .finally(() => {
        // Only reset loading state if not redirecting to checkout
        const proceedToCheckout = this.elements.submitButton?.dataset.proceedToCheckout === 'true';
        if (!proceedToCheckout) {
          this.setLoadingState(false);
        }
      });
  }

  /**
   * Handle tab switching logic - supports both buttons and inputs
   */
  handleTabSwitch() {
    let selectedTab = null;
    let selectedType = null;

    // First try to find an active button
    const activeButton = this.querySelector('.purchase-options__tab-nav.active');
    if (activeButton) {
      selectedType = activeButton.dataset.purchaseType;
    }

    // Fall back to checked input
    if (!selectedType) {
      selectedTab = this.querySelector('.purchase-options__tab-input:checked');
      if (selectedTab) {
        selectedType = selectedTab.value;
      }
    }

    // If no selection found, default to subscription if available
    if (!selectedType) {
      const subscriptionButton = this.querySelector('.purchase-options__tab-nav[data-purchase-type="subscription"]');
      const subscriptionInput = this.querySelector('input[data-purchase-type="subscription"]');
      const onetimeButton = this.querySelector('.purchase-options__tab-nav[data-purchase-type="onetime"]');
      const onetimeInput = this.querySelector('input[data-purchase-type="onetime"]');

      if (subscriptionButton || subscriptionInput) {
        selectedType = 'subscription';
        if (subscriptionButton) {
          subscriptionButton.classList.add('active');
          subscriptionButton.setAttribute('aria-selected', 'true');
        }
        if (subscriptionInput) {
          subscriptionInput.checked = true;
        }
      } else if (onetimeButton || onetimeInput) {
        selectedType = 'onetime';
        if (onetimeButton) {
          onetimeButton.classList.add('active');
          onetimeButton.setAttribute('aria-selected', 'true');
        }
        if (onetimeInput) {
          onetimeInput.checked = true;
        }
      }
    }

    if (!selectedType) return;

    const allContent = this.querySelectorAll('.purchase-options__content');

    // Hide all content sections
    allContent.forEach(content => {
      content.classList.remove('active');
    });

    // Show the selected content section and select first option
    if (selectedType === 'subscription') {
      const subscriptionContent = this.querySelector('.purchase-options__content--subscription');
      if (subscriptionContent) subscriptionContent.classList.add('active');

      // Select the first subscription option (top option)
      this.selectHighestQuantityOption('.subscription-option__input', '.subscription-option');
    } else {
      const onetimeContent = this.querySelector('.purchase-options__content--onetime');
      if (onetimeContent) onetimeContent.classList.add('active');

      // Select the first onetime option (top option)
      this.selectHighestQuantityOption('.onetime-option__input', '.onetime-option');
    }
  }

  /**
   * Explicitly select subscription tab and trigger all necessary updates
   */
  selectSubscription() {
    try {
      console.log('=== Starting selectSubscription for element:', this.id, 'isActive:', this.isActive);

      // Re-detect active state to ensure we have latest state
      this.detectActiveState();
      console.log('Active state after detection:', this.isActive);

      // Look for both button and input subscription tabs
      const subscriptionButton = this.querySelector('.purchase-options__tab-nav[data-purchase-type="subscription"]');
      const subscriptionInput = this.querySelector('input[data-purchase-type="subscription"]');
      const onetimeButton = this.querySelector('.purchase-options__tab-nav[data-purchase-type="onetime"]');
      const onetimeInput = this.querySelector('input[data-purchase-type="onetime"]');

      if (!subscriptionButton && !subscriptionInput) {
        console.error('No subscription tab found for element:', this.id);
        return;
      }

      console.log('Found subscription tab, proceeding with selection');

      // Step 1: Reset all tabs - handle both buttons and inputs

      // Reset button states
      this.elements.tabButtons.forEach(btn => {
        btn.classList.remove('active');
        btn.setAttribute('aria-selected', 'false');
      });

      // Reset input states
      this.elements.tabInputs.forEach(input => {
        input.checked = false;
      });

      // Activate subscription tab
      if (subscriptionButton) {
        subscriptionButton.classList.add('active');
        subscriptionButton.setAttribute('aria-selected', 'true');
      }
      if (subscriptionInput) {
        subscriptionInput.checked = true;
      }

      // Step 2: Show/hide content sections smoothly
      const subscriptionContent = this.querySelector('.purchase-options__content--subscription');
      const onetimeContent = this.querySelector('.purchase-options__content--onetime');

      // Add smooth transition styles if not already present
      if (subscriptionContent && !subscriptionContent.style.transition) {
        subscriptionContent.style.transition = 'opacity 0.1s ease-in-out';
      }
      if (onetimeContent && !onetimeContent.style.transition) {
        onetimeContent.style.transition = 'opacity 0.1s ease-in-out';
      }

      if (subscriptionContent) {
        subscriptionContent.classList.add('active');
        console.log('Showed subscription content');
      }
      if (onetimeContent) {
        onetimeContent.classList.remove('active');
        console.log('Hid onetime content');
      }

      // Step 3: Select subscription option and trigger events immediately
      try {
        const selectedOption = this.selectHighestQuantityOption('.subscription-option__input', '.subscription-option');
        if (selectedOption) {
          console.log('Selected subscription option:', selectedOption.dataset.quantity || selectedOption.value);

          // Trigger events immediately to prevent visual delays
          try {
            // Trigger change event on the subscription option
            const optionChangeEvent = new Event('change', { bubbles: true });
            selectedOption.dispatchEvent(optionChangeEvent);
            console.log('Dispatched change event on subscription option');

            // Trigger appropriate tab change event
            if (subscriptionButton) {
              // For buttons, trigger click event
              const buttonClickEvent = new Event('click', { bubbles: true });
              subscriptionButton.dispatchEvent(buttonClickEvent);
              console.log('Dispatched click event on subscription button');
            }
            if (subscriptionInput) {
              // For inputs, trigger change event
              const inputChangeEvent = new Event('change', { bubbles: true });
              subscriptionInput.dispatchEvent(inputChangeEvent);
              console.log('Dispatched change event on subscription input');
            }

          } catch (eventError) {
            console.error('Error dispatching events:', eventError);
          }

        } else {
          console.warn('No subscription option was selected');
        }
      } catch (optionError) {
        console.error('Error selecting subscription option:', optionError);
      }

      // Step 4: Immediate purchase type update
      try {
        console.log('Immediate purchase type update');
        this.updatePurchaseType();
        console.log('Purchase type update completed');
      } catch (updateError) {
        console.error('Error updating purchase type:', updateError);
        // Retry once after a very short delay
        setTimeout(() => {
          try {
            this.updatePurchaseType();
          } catch (retryError) {
            console.error('Retry failed:', retryError);
          }
        }, 10);
      }

      console.log('=== selectSubscription completed for element:', this.id);

    } catch (error) {
      console.error('Error in selectSubscription for element:', this.id, error);
    }
  }

  /**
   * Select the first option (top option in the list)
   */
  selectFirstOption(optionSelector, containerClass) {
    const options = this.querySelectorAll(optionSelector);

    if (options.length === 0) return null;

    // Uncheck all options and remove selected class
    options.forEach(option => {
      option.checked = false;
      const container = option.closest(containerClass);
      if (container) {
        container.classList.remove(containerClass.replace('.', '') + '--selected');
      }
    });

    // Select the first option
    const firstOption = options[0];
    firstOption.checked = true;
    const container = firstOption.closest(containerClass);
    if (container) {
      container.classList.add(containerClass.replace('.', '') + '--selected');
    }

    return firstOption;
  }

  /**
   * Select the highest quantity option
   */
  selectHighestQuantityOption(optionSelector, containerClass) {
    const options = this.querySelectorAll(optionSelector);
    let highestQuantityOption = null;
    let highestQuantity = 0;

    options.forEach(option => {
      const quantity = parseInt(option.dataset.quantity) || 0;
      if (quantity > highestQuantity) {
        highestQuantity = quantity;
        highestQuantityOption = option;
      }
    });

    if (highestQuantityOption) {
      // Uncheck all options and remove selected class
      options.forEach(option => {
        option.checked = false;
        option
          .closest(containerClass)
          .classList.remove(containerClass.replace('.', '') + '--selected');
      });

      // Select the highest quantity option
      highestQuantityOption.checked = true;
      highestQuantityOption
        .closest(containerClass)
        .classList.add(containerClass.replace('.', '') + '--selected');

      // Trigger change event on the selected option immediately
      const changeEvent = new Event('change', { bubbles: true });
      highestQuantityOption.dispatchEvent(changeEvent);

      console.log('Selected highest quantity option:', highestQuantityOption.dataset.quantity || highestQuantityOption.value);
    }

    return highestQuantityOption;
  }


  /**
   * Update purchase type and pricing
   */
  updatePurchaseType() {
    let selectedType = null;

    // First try to find active button
    const activeButton = this.querySelector('.purchase-options__tab-nav.active');
    if (activeButton) {
      selectedType = activeButton.dataset.purchaseType;
    }

    // Fall back to checked input
    if (!selectedType) {
      const selectedTab = this.querySelector('.purchase-options__tab-input:checked');
      if (selectedTab) {
        selectedType = selectedTab.value;
      }
    }

    if (!selectedType) {
      console.log('No selected tab found, cannot update purchase type for element:', this.id);
      return;
    }

    console.log('Updating purchase type:', selectedType, 'for element:', this.id);

    if (selectedType === 'subscription') {
      this.updateSubscriptionPurchase();
    } else {
      this.updateOnetimePurchase();
    }

    // Trigger custom event for other scripts
    window.dispatchEvent(
      new CustomEvent('purchase-option:change', {
        detail: {
          type: selectedType,
          variantId: this.elements.variantInput ? this.elements.variantInput.value : '',
          sellingPlan: this.elements.sellingPlanInput ? this.elements.sellingPlanInput.value : '',
          quantity: this.elements.quantityInput ? this.elements.quantityInput.value : '1',
          months: this.elements.monthsInput ? this.elements.monthsInput.value : ''
        }
      })
    );

    // Update URL
    if (this.isActive) {
        this.updateUrl(
            this.elements.variantInput ? this.elements.variantInput.value : '',
            this.elements.sellingPlanInput ? this.elements.sellingPlanInput.value : '',
            this.elements.quantityInput ? this.elements.quantityInput.value : '1'
        );
    }
  }

  /**
   * Update for subscription purchase
   */
  updateSubscriptionPurchase() {
    const selectedOption = this.querySelector('.subscription-option__input:checked');

    if (selectedOption) {
      console.log('Updating subscription purchase for element:', this.id, 'isActive:', this.isActive);

      // Only update main form inputs if this element is active
      if (this.isActive && this.elements.variantInput && this.elements.sellingPlanInput && this.elements.quantityInput) {
        // For subscription: use variant ID and selling plan, quantity = 1
        this.elements.variantInput.value = selectedOption.dataset.variantId || selectedOption.value;
        this.elements.sellingPlanInput.value = selectedOption.dataset.sellingPlan || '';
        this.elements.quantityInput.value = '1'; // Always 1 for subscription variants

        // Set the months custom property for subscriptions
        if (this.elements.monthsInput) {
          this.elements.monthsInput.setAttribute('name', 'properties[_months]');
          this.elements.monthsInput.value = selectedOption.dataset.months || selectedOption.dataset.quantity || '';
        }

        console.log('Updated main form inputs:', {
          variantId: this.elements.variantInput.value,
          sellingPlan: this.elements.sellingPlanInput.value,
          quantity: this.elements.quantityInput.value,
          months: this.elements.monthsInput ? this.elements.monthsInput.value : 'N/A'
        });
      }

      // Always update pricing display for active elements
      if (this.isActive) {
        const planPrice = selectedOption.getAttribute('data-plan-price');
        const planComparePrice = selectedOption.getAttribute('data-plan-compare-price');
        this.updatePricingDisplay(planPrice, planComparePrice);
      }
    }
  }

  /**
   * Update for one-time purchase
   */
  updateOnetimePurchase() {
    const selectedOption = this.querySelector('.onetime-option__input:checked');

    if (selectedOption) {
      console.log('Updating one-time purchase for element:', this.id, 'isActive:', this.isActive);

      // Only update main form inputs if this element is active
      if (this.isActive && this.elements.variantInput && this.elements.sellingPlanInput && this.elements.quantityInput) {
        // For one-time: use variant ID, no selling plan, quantity from option
        this.elements.variantInput.value = selectedOption.dataset.variantId || selectedOption.value;
        this.elements.sellingPlanInput.value = ''; // No selling plan for one-time
        this.elements.quantityInput.value = selectedOption.dataset.quantity || 1;

        // For one-time: use OTP property and set to selected quantity
        if (this.elements.monthsInput) {
          this.elements.monthsInput.setAttribute('name', 'properties[_otp]');
          this.elements.monthsInput.value = selectedOption.dataset.quantity || '';
        }

        console.log('Updated main form inputs:', {
          variantId: this.elements.variantInput.value,
          sellingPlan: this.elements.sellingPlanInput.value,
          quantity: this.elements.quantityInput.value,
          otp: this.elements.monthsInput ? this.elements.monthsInput.value : 'N/A'
        });
      }

      // Always update pricing display for active elements
      if (this.isActive) {
        const totalPrice = selectedOption.getAttribute('data-total-price');
        const comparePrice = selectedOption.getAttribute('data-compare-price');
        this.updatePricingDisplay(totalPrice, comparePrice);
      }
    }
  }

  /**
   * Format money with fallback for when Shopify.formatMoney is not available
   */
  formatMoney(cents) {
    // Check if Shopify.formatMoney is available
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents);
    }

    // Fallback: simple dollar formatting
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }

    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }

  /**
   * Update pricing display
   */
  updatePricingDisplay(totalPrice, comparePrice) {
    if (this.elements.totalPriceElement && totalPrice) {
      this.elements.totalPriceElement.textContent = this.formatMoney(totalPrice);
    }

    if (this.elements.buttonPrice && totalPrice) {
      this.elements.buttonPrice.textContent = this.formatMoney(totalPrice);
    }

    if (comparePrice && parseInt(comparePrice) > parseInt(totalPrice)) {
      const savings = parseInt(comparePrice) - parseInt(totalPrice);

      if (this.elements.originalPriceElement && this.elements.originalAmountElement) {
        this.elements.originalAmountElement.textContent = this.formatMoney(comparePrice);
        this.elements.originalPriceElement.style.display = 'block';
      }

      if (this.elements.savingsElement && this.elements.savingsAmountElement) {
        this.elements.savingsAmountElement.textContent = this.formatMoney(savings);
        this.elements.savingsElement.style.display = 'block';
      }

      if (this.elements.buttonComparePrice) {
        this.elements.buttonComparePrice.textContent = this.formatMoney(comparePrice);
        this.elements.buttonComparePrice.style.display = 'inline';
      }
    } else {
      if (this.elements.originalPriceElement) this.elements.originalPriceElement.style.display = 'none';
      if (this.elements.savingsElement) this.elements.savingsElement.style.display = 'none';
      if (this.elements.buttonComparePrice) this.elements.buttonComparePrice.style.display = 'none';
    }
  }

  /**
   * Set loading state
   */
  setLoadingState(loading) {
    if (loading) {
      if (this.elements.submitButton) this.elements.submitButton.disabled = true;
      if (this.elements.buttonSpinner) this.elements.buttonSpinner.classList.remove('hidden');
      if (this.elements.buttonText) this.elements.buttonText.style.opacity = '0.7';
    } else {
      if (this.elements.submitButton) this.elements.submitButton.disabled = false;
      if (this.elements.buttonSpinner) this.elements.buttonSpinner.classList.add('hidden');
      if (this.elements.buttonText) this.elements.buttonText.style.opacity = '1';
    }
  }

  /**
   * Show success notification
   */
  showSuccessNotification(cartItem) {
    if (this.elements.cartNotificationWrapper && this.elements.cartNotificationProductName) {
      this.elements.cartNotificationProductName.textContent = cartItem.product_title || 'Product';
      this.elements.cartNotificationWrapper.style.display = 'block';

      // Auto hide after 3 seconds
      setTimeout(() => {
        this.elements.cartNotificationWrapper.style.display = 'none';
      }, 3000);
    }
  }

  /**
   * Show error notification
   */
  showErrorNotification(message) {
    if (this.elements.errorNotificationWrapper && this.elements.errorNotificationText) {
      this.elements.errorNotificationText.textContent = message;
      this.elements.errorNotificationWrapper.style.display = 'block';

      // Auto hide after 5 seconds
      setTimeout(() => {
        this.elements.errorNotificationWrapper.style.display = 'none';
      }, 5000);
    }
  }

  /**
   * Hide all notifications
   */
  hideNotifications() {
    if (this.elements.cartNotificationWrapper) this.elements.cartNotificationWrapper.style.display = 'none';
    if (this.elements.errorNotificationWrapper) this.elements.errorNotificationWrapper.style.display = 'none';
  }

  /**
   * Handle variant changes
   */
  handleVariantChange() {
    const el = document.querySelector('[data-selected-variant]');
    if (!el) return;
    const variantData = JSON.parse(el.textContent);

    if (this.elements.variantInput && variantData) {
      this.elements.variantInput.value = variantData.id;
    }

    // Refresh pricing based on current selection
    this.updatePurchaseType();
  }

  /**
   * Initialize options on load
   */
  initializeOptions() {
    console.log('Initializing options for element:', this.id, 'isActive:', this.isActive);

    // Check if alternate layout mode (force default variant)
    const isAlternateLayout = this.dataset.selectLowest === 'true';

    // Check for URL parameters to set initial state
    const urlParams = new URLSearchParams(window.location.search);
    let variantId = urlParams.get('variant');
    let quantity = urlParams.get('quantity'); // Standard Shopify param
    let otp = urlParams.get('otp'); // User suggested fallback

    // When alternate layout is enabled, ALWAYS force the default variant (ignore URL)
    if (isAlternateLayout && this.dataset.defaultVariantId) {
      variantId = this.dataset.defaultVariantId;
      quantity = null; // Clear quantity to match default
      otp = null;
      console.log('Alternate layout: Forcing default variant:', variantId);
    } else if (!variantId && this.dataset.defaultVariantId) {
      // Normal mode: Check for default variant from settings if not in URL
      variantId = this.dataset.defaultVariantId;
      console.log('Using default variant from settings:', variantId);
    }

    let matchedOption = null;
    let targetType = null;

    if (variantId) {
      // Try to find a matching option
      const allOptions = this.querySelectorAll('.subscription-option__input, .onetime-option__input');
      
      for (const option of allOptions) {
        const optVariantId = option.dataset.variantId || option.value;
        const optQuantity = option.dataset.quantity || '1';
        
        // Check if variant matches
        if (optVariantId === variantId) {
          // If quantity/otp is provided, it must match
          // (Use loosely equal for string/number comparison)
          if ((quantity || otp) && optQuantity != (quantity || otp)) continue;

          // Found a match!
          matchedOption = option;
          targetType = option.classList.contains('subscription-option__input') ? 'subscription' : 'onetime';
          
          // If we have an exact match on quantity, stop looking.
          if (quantity || otp) break;
        }
      }
    }

    if (matchedOption && targetType) {
      console.log('Found matching option from URL:', { variantId, quantity, targetType });
      
      // Select the option
      matchedOption.checked = true;
      
      // Update UI for the selected option
      if (targetType === 'subscription') {
        this.querySelectorAll('.subscription-option').forEach(el => el.classList.remove('subscription-option--selected'));
        matchedOption.closest('.subscription-option').classList.add('subscription-option--selected');
        
        // Manually handle tab switch to avoid selectSubscription() overriding our specific option selection
        const subscriptionButton = this.querySelector('.purchase-options__tab-nav[data-purchase-type="subscription"]');
        const subscriptionInput = this.querySelector('input[data-purchase-type="subscription"]');
        
        // Reset tabs
        this.elements.tabButtons.forEach(btn => {
            btn.classList.remove('active');
            btn.setAttribute('aria-selected', 'false');
        });
        this.elements.tabInputs.forEach(input => input.checked = false);

        // Activate subscription tab
        if (subscriptionButton) {
            subscriptionButton.classList.add('active');
            subscriptionButton.setAttribute('aria-selected', 'true');
        }
        if (subscriptionInput) subscriptionInput.checked = true;

        // Show content
        const subscriptionContent = this.querySelector('.purchase-options__content--subscription');
        const onetimeContent = this.querySelector('.purchase-options__content--onetime');
        
        if (subscriptionContent) subscriptionContent.classList.add('active');
        if (onetimeContent) onetimeContent.classList.remove('active');

        // Trigger update
        this.updatePurchaseType();
      } else {
        this.querySelectorAll('.onetime-option').forEach(el => el.classList.remove('onetime-option--selected'));
        matchedOption.closest('.onetime-option').classList.add('onetime-option--selected');
        
        // Switch to one-time tab
        const onetimeButton = this.querySelector('.purchase-options__tab-nav[data-purchase-type="onetime"]');
        if (onetimeButton) {
             // Trigger click to handle UI switching
             const buttonClickEvent = new Event('click', { bubbles: true });
             onetimeButton.dispatchEvent(buttonClickEvent);
        } else {
            // Fallback if no button (e.g. only input)
            this.handleTabSwitch(); 
        }
      }
    } else {
      // Default behavior if no URL params or no match found
      
      // Select options for both subscription and one-time
      this.selectHighestQuantityOption('.subscription-option__input', '.subscription-option');
      this.selectHighestQuantityOption('.onetime-option__input', '.onetime-option');

      this.selectSubscription();
    }

    // Re-check active state after a delay in case product toggle script runs later
    setTimeout(() => {
      const wasActive = this.isActive;
      this.detectActiveState();

      // If we just became active, make sure subscription is selected and pricing is updated
      if (!wasActive && this.isActive) {
        console.log('Element became active after delayed detection:', this.id);
        // Only force subscription if we didn't match from URL
        if (!matchedOption) {
             this.selectSubscription();
        } else {
             this.updatePurchaseType();
        }
      } else if (this.isActive) {
        console.log('Element was already active, ensuring pricing is updated:', this.id);
        this.updatePurchaseType();
      }
    }, 200);
  }

  /**
   * Update URL with current selection
   */
  updateUrl(variantId, sellingPlan, quantity) {
    // Prevent URL updates if inside a quick add modal or on a collection page
    const isQuickAdd = this.closest('[data-quick-add-modal]');
    const isCollectionPage = document.body.classList.contains('template-collection');
    // Prevent URL updates when alternate layout is enabled (forces default on each page load)
    const isAlternateLayout = this.dataset.selectLowest === 'true';
    
    if (isQuickAdd || isCollectionPage || isAlternateLayout) {
      return;
    }

    if (!window.history.replaceState) return;

    const url = new URL(window.location.href);
    const params = url.searchParams;

    if (variantId) {
      params.set('variant', variantId);
    } else {
      params.delete('variant');
    }

    // Always remove selling_plan to keep URLs clean
    params.delete('selling_plan');

    // Handle quantity - only add if > 1
    if (quantity && quantity !== '1') {
      params.set('quantity', quantity);
    } else {
        params.delete('quantity');
    }
    
    // Remove OTP param if we are using quantity now, to clean up
    params.delete('otp');

    const newUrl = `${url.pathname}${params.toString() ? '?' + params.toString() : ''}${url.hash}`;
    
    // Only update if changed to avoid spamming history
    if (newUrl !== window.location.href) {
        window.history.replaceState({ path: newUrl }, '', newUrl);
    }
  }

  /**
   * Reset to default state when switching products
   */
  resetToDefaultState(defaultToSubscription = true) {
    console.log('Resetting purchase option to default state:', this.id, 'defaultToSubscription:', defaultToSubscription);

    // Always select subscription when resetting (force subscription behavior)
    this.selectSubscription();
  }

  /**
   * Setup mutation observer to watch for active class changes
   */
  setupMutationObserver() {
    this.mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          this.handleActiveStateChange();
        }
      });
    });

    this.mutationObserver.observe(this, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  /**
   * Handle when the active class changes on this element
   */
  handleActiveStateChange() {
    const wasActive = this.isActive;
    this.detectActiveState();

    // If this element just became active, update inputs and pricing
    if (!wasActive && this.isActive) {
      console.log('Purchase option became active:', this.id);
      // Use setTimeout to ensure the DOM updates are complete
      setTimeout(() => {
        this.updatePurchaseType();
      }, 50);
    }
  }

  /**
   * Handle external product changes (from product toggle outside this element)
   */
  handleExternalProductChange(e) {
    // Handle product-tab:switched event specifically
    if (e.type === 'product-tab:switched') {
      const { purchaseOptionsElement } = e.detail;

      // Check if this event is for this specific purchase options element
      if (purchaseOptionsElement === this) {
        console.log('This purchase option was switched to, updating state...');

        // Re-detect active state and update if needed
        this.detectActiveState();

        // Force update purchase type since we just became active
        if (this.isActive) {
          // Use setTimeout to ensure all DOM updates are complete
          setTimeout(() => {
            this.updatePurchaseType();
          }, 100);
        }
      }
    } else {
      // Handle other events like product-tab:change or purchase-option:activate
      console.log('External product change event received:', e.type, e.detail);

      // Re-detect active state for any external change
      this.detectActiveState();

      // If we're now active, update our state
      if (this.isActive) {
        setTimeout(() => {
          this.updatePurchaseType();
        }, 50);
      }
    }
  }
}

// Register the custom element
customElements.define('purchase-options', PurchaseOptionsElement);


 